import { gql } from "@apollo/client";
import { ActorRefFrom } from "xstate";
import { createModel } from "xstate/lib/model";

import { graphQLClient } from "../../api/graphql";
import { openReportURL, SelectedSupplier, SupplierFilter } from "../generateReportConfig.machine";

export interface FileReferenceFilter {
  from: string;
  to: string;
}

export interface InvoiceReportActor extends ActorRefFrom<typeof invoiceReportMachine> {}

export const invoiceReportModel = createModel(
  {
    supplierFilter: undefined as SupplierFilter | undefined,
    fileReference: undefined as FileReferenceFilter | undefined,
    reportURL: undefined as string | undefined,
    error: undefined as string | undefined,
  },
  {
    events: {
      provideSupplierFilter: (value: { from: SelectedSupplier; to: SelectedSupplier }) => ({
        data: value,
      }),
      provideFileReferenceFilter: (value: FileReferenceFilter) => ({
        data: value,
      }),
      skipStep: () => ({}),
      goBack: () => ({}),
      generateReport: () => ({}),
      reportGenerationSuccess: (value: string) => ({ data: { reportURL: value } }),
      reportGenerationFailure: (value: string) => ({ data: { error: value } }),
      restart: () => ({}),
    },
  }
);

export const invoiceReportMachine = invoiceReportModel.createMachine(
  {
    context: invoiceReportModel.initialContext,
    initial: "configuringSupplierFilter",
    states: {
      configuringSupplierFilter: {
        id: "configuringSupplierFilter",
        on: {
          provideSupplierFilter: {
            actions: invoiceReportModel.assign({
              supplierFilter: (_, ev) => {
                return ev.data;
              },
            }),
            target: "configuringFileReferenceFilter",
          },
        },
      },
      configuringFileReferenceFilter: {
        id: "configuringFileReferenceFilter",
        on: {
          provideFileReferenceFilter: {
            actions: invoiceReportModel.assign({
              fileReference: (_, ev) => {
                return ev.data;
              },
            }),
            target: "showingReportConfigSummary",
          },
          goBack: {
            target: "configuringSupplierFilter",
          },
        },
      },
      showingReportConfigSummary: {
        initial: "idle",
        states: {
          idle: {
            on: {
              generateReport: {
                target: "generatingReport",
              },
              goBack: {
                target: "#configuringFileReferenceFilter",
              },
            },
          },
          generatingReport: {
            invoke: {
              id: "generatingReport",
              src: "generateReport",
              onError: {
                target: "#failure",
              },
            },
            on: {
              reportGenerationSuccess: {
                target: "#reportGenerated",
                actions: invoiceReportModel.assign({
                  reportURL: (_, ev) => ev.data.reportURL,
                }),
              },
              reportGenerationFailure: {
                target: "#failure",
                actions: invoiceReportModel.assign({
                  error: (_, ev) => ev.data.error,
                }),
              },
            },
          },
        },
      },
      reportGenerated: {
        id: "reportGenerated",
        entry: "openReportURL",
      },
      failure: {
        id: "failure",
        on: {
          restart: {
            target: "configuringSupplierFilter",
            actions: invoiceReportModel.assign(() => {
              return invoiceReportModel.initialContext;
            }),
          },
        },
      },
    },
  },
  {
    actions: {
      openReportURL: async (ctx) => {
        if (!ctx.reportURL) {
          throw new Error("Response does not contain a url");
        }

        openReportURL(ctx.reportURL);
      },
    },
    services: {
      generateReport: (ctx, ev) => (cb) => {
        graphQLClient
          .query({
            query: GENERATE_INVOICE_REPORT,
            variables: {
              input: {
                fromSupplierID: ctx.supplierFilter?.from.internalId,
                toSupplierID: ctx.supplierFilter?.to.internalId,
                fromFileReference: ctx.fileReference?.from,
                toFileReference: ctx.fileReference?.to,
              },
            },
          })
          .then((res) => {
            const reportUrl = res.data.invoiceReport.url;

            cb(invoiceReportModel.events.reportGenerationSuccess(reportUrl));
          })
          .catch((e) => {
            cb(invoiceReportModel.events.reportGenerationFailure(e.message));
          });
      },
    },
  }
);

const GENERATE_INVOICE_REPORT = gql`
  query GetInvoiceReportInfo($input: InvoiceReportInput!) {
    invoiceReport(input: $input) {
      url
    }
  }
`;
